import { BaseFeature } from '../../core/base-feature.js';

/**
 * TimeLimitFeature - App usage time limits (Frontend)
 * Handles: managing limits, viewing usage, justification logs
 */
export class TimeLimitFeature extends BaseFeature {
    constructor(coreAPI) {
        super(coreAPI);

        // State
        this.limits = [];
        this.usage = {};
        this.justifications = [];
        this.extensionRequests = [];
        this.currentApp = null;
        this.isLoading = false;
        this.isRunning = false;
        this.enforcementState = { activeOverlays: [] };

        // Time manipulation state
        this.manipulationConfigs = {};

        // Event listeners for React components
        this.updateCallbacks = new Set();
        this.usageCallbacks = new Set();
        this.stateCallbacks = new Set();
        this.extensionRequestCallbacks = new Set();
        this.justificationCallbacks = new Set();
        this.enforcementStateCallbacks = new Set();
        this.appBlockedCallbacks = new Set();
        this.manipulationCallbacks = new Set();

        // Pending promises for request/response
        this._pendingRequests = new Map();
    }

    static getMetadata() {
        return {
            id: 'time-limit',
            name: 'time-limit',
            version: 'v1.0',
            capabilities: [
                'manage_limits',
                'view_usage',
                'view_justifications',
                'start_stop_tracking'
            ],
            dependencies: []
        };
    }

    async initialize() {
        console.log('[TimeLimit] Initializing...');
        console.log('[TimeLimit] Initialized');
    }

    async handleMessage(message) {
        const { action, payload, messageId, fromId } = message;
        this._validateInbound(action, payload); // contract check (dev/test only)

        switch (action) {
            case 'get_limits_response':
                return this.handleGetLimitsResponse(payload);

            case 'add_limit_response':
                return this.handleAddLimitResponse(payload);

            case 'update_limit_response':
                return this.handleUpdateLimitResponse(payload);

            case 'remove_limit_response':
                return this.handleRemoveLimitResponse(payload);

            case 'get_usage_response':
                return this.handleGetUsageResponse(payload);

            case 'get_justifications_response':
                return this.handleGetJustificationsResponse(payload);

            case 'start_response':
                return this.handleStartResponse(payload);

            case 'stop_response':
                return this.handleStopResponse(payload);

            case 'usage_update':
                // Real-time usage update from desktop
                return this.handleUsageUpdate(payload);

            case 'state_changes':
                // State transition notifications
                return this.handleStateChanges(payload);

            case 'app_change':
                // Current app changed
                return this.handleAppChange(payload);

            case 'get_extension_requests_response':
                return this.handleGetExtensionRequestsResponse(payload);

            case 'approve_extension_response':
                return this.handleApproveExtensionResponse(payload);

            case 'deny_extension_response':
                return this.handleDenyExtensionResponse(payload);

            case 'clear_cooldown_response':
                return this.handleClearCooldownResponse(payload);

            case 'extension_request_update':
                return this.handleExtensionRequestUpdate(payload);

            case 'initial_state':
                return this.handleInitialState(payload);

            case 'new_justification':
                return this.handleNewJustification(payload);

            case 'remote_block_window_response':
                return this.handleRemoteBlockResponse(payload);

            case 'remote_unblock_window_response':
                return this.handleRemoteUnblockResponse(payload);

            case 'get_enforcement_state_response':
                return this.handleGetEnforcementStateResponse(payload);

            case 'get_active_lockout_response':
                return this._resolvePending('get_active_lockout', payload);

            case 'adjust_lockout_response':
                return this._resolvePending('adjust_lockout', payload);

            case 'end_lockout_response':
                return this._resolvePending('end_lockout', payload);

            case 'enforcement_state':
                return this.handleEnforcementStateUpdate(payload);

            case 'app_blocked':
                return this.handleAppBlocked(payload);

            case 'get_manipulation_configs_response':
                return this.handleGetManipulationConfigsResponse(payload);

            case 'set_manipulation_config_response':
                return this.handleSetManipulationConfigResponse(payload);

            case 'purchase_time_response':
                return this.handlePurchaseTimeResponse(payload);

            case 'apply_retroactive_limit_response':
                return this.handleApplyRetroactiveLimitResponse(payload);

            case 'manipulation_update':
                return this.handleManipulationUpdate(payload);

            case 'get_screen_time_overlay_config_response':
                return this.handleGetScreenTimeOverlayConfigResponse(payload);

            case 'set_screen_time_overlay_config_response':
                return this.handleSetScreenTimeOverlayConfigResponse(payload);

            default:
                console.warn(`[TimeLimit] Unknown action: ${action}`);
        }
    }

    // =====================================
    // Limit Management
    // =====================================

    /**
     * Request list of time limits
     */
    getLimits() {
        this.isLoading = true;
        console.log('[TimeLimit] Requesting limits...');
        return this.sendMessage('get_limits', {});
    }

    handleGetLimitsResponse(payload) {
        this.isLoading = false;

        if (!payload.success) {
            console.error('[TimeLimit] Failed to get limits:', payload.error);
            return;
        }

        this.limits = payload.limits || [];
        console.log(`[TimeLimit] Loaded ${this.limits.length} limits`);
        this.notifyUpdateListeners();
    }

    /**
     * Add a new time limit
     * @param {Object} config - Limit configuration
     * @returns {Promise<Object>} Result
     */
    async addLimit(config) {
        console.log('[TimeLimit] Adding limit:', config);

        return new Promise((resolve) => {
            const messageId = this.sendMessage('add_limit', config);
            this._pendingRequests.set('add_limit', resolve);
        });
    }

    handleAddLimitResponse(payload) {
        const resolve = this._pendingRequests.get('add_limit');
        if (resolve) {
            this._pendingRequests.delete('add_limit');
            resolve(payload);
        }

        if (payload.success) {
            console.log('[TimeLimit] Limit added successfully');
            this.getLimits();
        } else {
            console.error('[TimeLimit] Failed to add limit:', payload.error);
        }
    }

    /**
     * Update an existing limit
     * @param {number} id - Limit ID
     * @param {Object} config - Updated configuration
     * @returns {Promise<Object>} Result
     */
    async updateLimit(id, config) {
        console.log(`[TimeLimit] Updating limit ${id}:`, config);

        return new Promise((resolve) => {
            const messageId = this.sendMessage('update_limit', { id, config });
            this._pendingRequests.set('update_limit', resolve);
        });
    }

    handleUpdateLimitResponse(payload) {
        const resolve = this._pendingRequests.get('update_limit');
        if (resolve) {
            this._pendingRequests.delete('update_limit');
            resolve(payload);
        }

        if (payload.success) {
            console.log('[TimeLimit] Limit updated successfully');
            this.getLimits();
        } else {
            console.error('[TimeLimit] Failed to update limit:', payload.error);
        }
    }

    /**
     * Remove a limit
     * @param {number} id - Limit ID
     * @returns {Promise<Object>} Result
     */
    async removeLimit(id) {
        console.log(`[TimeLimit] Removing limit ${id}`);

        return new Promise((resolve) => {
            const messageId = this.sendMessage('remove_limit', { id });
            this._pendingRequests.set('remove_limit', resolve);
        });
    }

    handleRemoveLimitResponse(payload) {
        const resolve = this._pendingRequests.get('remove_limit');
        if (resolve) {
            this._pendingRequests.delete('remove_limit');
            resolve(payload);
        }

        if (payload.success) {
            console.log('[TimeLimit] Limit removed successfully');
            this.getLimits();
        } else {
            console.error('[TimeLimit] Failed to remove limit:', payload.error);
        }
    }

    // =====================================
    // Usage Tracking
    // =====================================

    /**
     * Request current usage data
     */
    getUsage() {
        console.log('[TimeLimit] Requesting usage...');
        return this.sendMessage('get_usage', {});
    }

    handleGetUsageResponse(payload) {
        if (!payload.success) {
            console.error('[TimeLimit] Failed to get usage:', payload.error);
            return;
        }

        this.usage = payload.usage || {};
        this.notifyUsageListeners();
    }

    handleUsageUpdate(payload) {
        if (!payload) return;

        if (!payload._isDelta) {
            // Full state — replace everything
            this.usage = payload;
        } else {
            // Delta — merge changed keys into existing usage
            for (const [key, value] of Object.entries(payload)) {
                if (key === '_isDelta' || key === '_timestamp' || key === '_activeApp') continue;
                if (key === 'focused') {
                    this.usage.focused = { ...(this.usage.focused || {}), ...value };
                } else if (key === 'running') {
                    this.usage.running = { ...(this.usage.running || {}), ...value };
                } else if (key === 'websites') {
                    if (!this.usage.websites) this.usage.websites = {};
                    for (const [browser, domains] of Object.entries(value)) {
                        this.usage.websites[browser] = { ...(this.usage.websites[browser] || {}), ...domains };
                    }
                } else {
                    // Top-level app usage keys (legacy spread from focusedUsage)
                    this.usage[key] = value;
                }
            }
        }

        // Store active app + timestamp for client-side extrapolation
        this._activeApp = payload._activeApp || null;
        this._lastUpdateTime = payload._timestamp || Date.now();

        this.notifyUsageListeners();

        // Start extrapolation timer if not running
        if (!this._extrapolationTimer && this._activeApp) {
            this._startExtrapolation();
        }
    }

    /**
     * Extrapolate active app usage between server updates.
     * Ticks every second, incrementing the active app's usage locally.
     */
    _startExtrapolation() {
        if (this._extrapolationTimer) return;
        this._extrapolationTimer = setInterval(() => {
            if (!this._activeApp) {
                clearInterval(this._extrapolationTimer);
                this._extrapolationTimer = null;
                return;
            }
            // Increment active app usage by 1 second
            const app = this._activeApp;
            if (this.usage[app] !== undefined) {
                this.usage[app] = (this.usage[app] || 0) + 1;
            }
            if (this.usage.focused && this.usage.focused[app] !== undefined) {
                this.usage.focused[app] = (this.usage.focused[app] || 0) + 1;
            }
            this.notifyUsageListeners();
        }, 1000);
    }

    /**
     * Get website usage organized by browser
     * @returns {Object} { 'firefox.exe': { 'youtube.com': 1800, ... }, ... }
     */
    getWebsiteUsage() {
        return this.usage.websites || {};
    }

    /**
     * Get website usage for a specific browser
     * @param {string} browserName - Browser executable name (e.g., 'firefox.exe')
     * @returns {Object} { 'youtube.com': 1800, 'reddit.com': 900, ... }
     */
    getWebsiteUsageForBrowser(browserName) {
        const websites = this.usage.websites || {};
        return websites[browserName.toLowerCase()] || {};
    }

    // =====================================
    // State Changes
    // =====================================

    handleStateChanges(payload) {
        console.log('[TimeLimit] State changes:', payload);
        this.notifyStateListeners(payload);
    }

    handleAppChange(payload) {
        this.currentApp = payload.appName;
        console.log(`[TimeLimit] Current app: ${this.currentApp}`);
        this.notifyUpdateListeners();
    }

    // =====================================
    // Justification Log
    // =====================================

    /**
     * Request justification log
     * @param {Object} options - Query options
     */
    getJustifications(options = {}) {
        console.log('[TimeLimit] Requesting justifications...');
        return this.sendMessage('get_justifications', options);
    }

    handleGetJustificationsResponse(payload) {
        if (!payload.success) {
            console.error('[TimeLimit] Failed to get justifications:', payload.error);
            return;
        }

        this.justifications = payload.justifications || [];
        console.log(`[TimeLimit] Loaded ${this.justifications.length} justifications`);
        this.notifyUpdateListeners();
    }

    // =====================================
    // Start/Stop Tracking
    // =====================================

    /**
     * Start time limit tracking
     */
    start() {
        console.log('[TimeLimit] Starting tracking...');
        return this.sendMessage('start', {});
    }

    handleStartResponse(payload) {
        if (payload.success) {
            this.isRunning = true;
            console.log('[TimeLimit] Tracking started');
        } else {
            console.error('[TimeLimit] Failed to start:', payload.error);
        }
        this.notifyUpdateListeners();
    }

    /**
     * Stop time limit tracking
     */
    stop() {
        console.log('[TimeLimit] Stopping tracking...');
        return this.sendMessage('stop', {});
    }

    handleStopResponse(payload) {
        if (payload.success) {
            this.isRunning = false;
            console.log('[TimeLimit] Tracking stopped');
        } else {
            console.error('[TimeLimit] Failed to stop:', payload.error);
        }
        this.notifyUpdateListeners();
    }

    // =====================================
    // Debug Controls
    // =====================================

    /**
     * Force exit lockout (debug)
     * @param {string} targetName - App name
     */
    debugExitLockout(targetName) {
        console.log(`[TimeLimit] Debug: Exit lockout for ${targetName}`);
        return this.sendMessage('debug_exit_lockout', { targetName });
    }

    /**
     * Add bonus time (debug)
     * @param {string} targetName - App name
     * @param {number} seconds - Seconds to add
     */
    debugAddTime(targetName, seconds) {
        console.log(`[TimeLimit] Debug: Add ${seconds}s for ${targetName}`);
        return this.sendMessage('debug_add_time', { targetName, seconds });
    }

    // =====================================
    // Remote Control (from Screen Share)
    // =====================================

    /**
     * Remote block a window by hwnd (preferred) or screen coordinates (fallback)
     * Used when user clicks Block button on a window in the screen share
     * @param {Object} params - Block parameters
     * @param {string} params.hwnd - Window handle (preferred - avoids race conditions)
     * @param {number} params.x - Screen X coordinate (fallback if hwnd not provided)
     * @param {number} params.y - Screen Y coordinate (fallback if hwnd not provided)
     * @param {string} params.reason - Reason for blocking
     * @param {boolean} params.createLimit - If true, creates a time limit
     * @returns {Promise<Object>} Result with success, windowInfo
     */
    async remoteBlockWindow(params = {}) {
        const { hwnd, x, y, reason, createLimit } = params;
        console.log(`[TimeLimit] Remote block window - hwnd: ${hwnd}, pos: (${x}, ${y})`);

        return new Promise((resolve) => {
            this.sendMessage('remote_block_window', { hwnd, x, y, reason, createLimit });
            this._pendingRequests.set('remote_block_window', resolve);
        });
    }

    /**
     * Remote unblock a window by app name
     * @param {string} appName - Application name to unblock
     * @returns {Promise<Object>} Result
     */
    async remoteUnblockWindow(appName) {
        console.log(`[TimeLimit] Remote unblock window: ${appName}`);

        return new Promise((resolve) => {
            this.sendMessage('remote_unblock_window', { appName });
            this._pendingRequests.set('remote_unblock_window', resolve);
        });
    }

    /**
     * Get current enforcement state
     * @returns {Promise<Object>} Enforcement state with activeOverlays array
     */
    async getEnforcementState() {
        console.log('[TimeLimit] Getting enforcement state');

        return new Promise((resolve) => {
            this.sendMessage('get_enforcement_state', {});
            this._pendingRequests.set('get_enforcement_state', resolve);
        });
    }

    /** Generic pending-request resolver for the simple request/response actions. */
    _resolvePending(key, payload) {
        const resolve = this._pendingRequests.get(key);
        if (resolve) { this._pendingRequests.delete(key); resolve(payload); }
    }

    /** Active forced-lockout status ({ lockout } or { lockout: null }). */
    async getActiveLockout() {
        return new Promise((resolve) => {
            this.sendMessage('get_active_lockout', {});
            this._pendingRequests.set('get_active_lockout', resolve);
        });
    }

    /** Extend (+) or shorten (−) the active lockout by deltaSeconds. */
    async adjustLockout(deltaSeconds) {
        return new Promise((resolve) => {
            this.sendMessage('adjust_lockout', { deltaSeconds });
            this._pendingRequests.set('adjust_lockout', resolve);
        });
    }

    /** End the active lockout now (user or AI inflicted). */
    async endLockout() {
        return new Promise((resolve) => {
            this.sendMessage('end_lockout', {});
            this._pendingRequests.set('end_lockout', resolve);
        });
    }

    // =====================================
    // Extension Requests
    // =====================================

    /**
     * Get extension requests
     * @param {Object} options - Query options (status, limit, offset)
     */
    getExtensionRequests(options = {}) {
        console.log('[TimeLimit] Requesting extension requests...');
        return this.sendMessage('get_extension_requests', options);
    }

    handleGetExtensionRequestsResponse(payload) {
        if (!payload.success) {
            console.error('[TimeLimit] Failed to get extension requests:', payload.error);
            return;
        }

        this.extensionRequests = payload.requests || [];
        console.log(`[TimeLimit] Loaded ${this.extensionRequests.length} extension requests`);
        this.notifyUpdateListeners();
    }

    /**
     * Approve an extension request
     * @param {number} requestId - Request ID
     * @param {number} grantedSeconds - Seconds to grant
     * @param {string} note - Optional note
     * @param {boolean} showAmount - Whether to display the granted time on the desktop overlay
     * @returns {Promise<Object>} Result
     */
    async approveExtension(requestId, grantedSeconds, note = null, showAmount = false) {
        console.log(`[TimeLimit] Approving extension request ${requestId}: +${grantedSeconds}s (showAmount: ${showAmount})`);

        return new Promise((resolve) => {
            this.sendMessage('approve_extension', { requestId, grantedSeconds, note, showAmount });
            this._pendingRequests.set('approve_extension', resolve);
        });
    }

    handleApproveExtensionResponse(payload) {
        const resolve = this._pendingRequests.get('approve_extension');
        if (resolve) {
            this._pendingRequests.delete('approve_extension');
            resolve(payload);
        }

        if (payload.success) {
            console.log('[TimeLimit] Extension request approved');
            this.getExtensionRequests();
        } else {
            console.error('[TimeLimit] Failed to approve extension:', payload.error);
        }
    }

    /**
     * Deny an extension request
     * @param {number} requestId - Request ID
     * @param {number|null} cooldownHours - Hours before user can request again
     * @param {string} note - Optional note
     * @returns {Promise<Object>} Result
     */
    async denyExtension(requestId, cooldownHours = null, note = null) {
        console.log(`[TimeLimit] Denying extension request ${requestId}`);

        return new Promise((resolve) => {
            this.sendMessage('deny_extension', { requestId, cooldownHours, note });
            this._pendingRequests.set('deny_extension', resolve);
        });
    }

    handleDenyExtensionResponse(payload) {
        const resolve = this._pendingRequests.get('deny_extension');
        if (resolve) {
            this._pendingRequests.delete('deny_extension');
            resolve(payload);
        }

        if (payload.success) {
            console.log('[TimeLimit] Extension request denied');
            this.getExtensionRequests();
        } else {
            console.error('[TimeLimit] Failed to deny extension:', payload.error);
        }
    }

    /**
     * Clear extension request cooldown for a target
     * @param {string} targetType - 'app' or 'session'
     * @param {string} targetName - App name or 'daily_session'
     * @returns {Promise<Object>} Result
     */
    async clearCooldown(targetType, targetName) {
        console.log(`[TimeLimit] Clearing cooldown for ${targetType}/${targetName}`);

        return new Promise((resolve) => {
            this.sendMessage('clear_cooldown', { targetType, targetName });
            this._pendingRequests.set('clear_cooldown', resolve);
        });
    }

    handleClearCooldownResponse(payload) {
        const resolve = this._pendingRequests.get('clear_cooldown');
        if (resolve) {
            this._pendingRequests.delete('clear_cooldown');
            resolve(payload);
        }

        if (payload.success) {
            console.log('[TimeLimit] Cooldown cleared');
            this.getExtensionRequests();
        } else {
            console.error('[TimeLimit] Failed to clear cooldown:', payload.error);
        }
    }

    /**
     * Handle live extension request update from desktop
     */
    handleExtensionRequestUpdate(payload) {
        console.log('[TimeLimit] handleExtensionRequestUpdate called with payload:', payload);
        const { action, request, targetType, targetName } = payload;

        if (action === 'new_request' && request) {
            console.log(`[TimeLimit] Live update: new extension request ${request.id}`);

            // Add to beginning of list (most recent first)
            this.extensionRequests = [request, ...this.extensionRequests];

            // Notify listeners
            this.notifyExtensionRequestListeners();
        } else if ((action === 'approved' || action === 'denied') && request) {
            console.log(`[TimeLimit] Live update: extension request ${request.id} ${action}`);

            // Update the request in our list
            const index = this.extensionRequests.findIndex(r => r.id === request.id);
            if (index !== -1) {
                this.extensionRequests[index] = request;
            }

            // Notify listeners
            this.notifyExtensionRequestListeners();
        } else if (action === 'cooldown_cleared') {
            console.log(`[TimeLimit] Live update: cooldown cleared for ${targetType}/${targetName}`);

            // Update the requests in our list to clear denialCooldownUntil
            this.extensionRequests = this.extensionRequests.map(req => {
                if (req.targetType === targetType && req.targetName === targetName && req.denialCooldownUntil) {
                    return { ...req, denialCooldownUntil: null };
                }
                return req;
            });

            // Notify listeners
            this.notifyExtensionRequestListeners();
        }
    }

    /**
     * Handle initial state from desktop (sent when DataChannel opens)
     */
    handleInitialState(payload) {
        console.log('[TimeLimit] Received initial state from desktop');

        const { isRunning, limits, usage, justifications, extensionRequests, manipulationConfigs } = payload;

        this.isRunning = isRunning || false;
        this.limits = limits || [];
        this.usage = usage || {};
        this.justifications = justifications || [];
        this.extensionRequests = extensionRequests || [];
        this.manipulationConfigs = manipulationConfigs || {};
        this.isLoading = false;

        console.log(`[TimeLimit] Initial state: ${this.limits.length} limits, running=${this.isRunning}, ${this.justifications.length} justifications, ${this.extensionRequests.length} requests`);

        // Notify all listeners
        this.notifyUpdateListeners();
        this.notifyUsageListeners();
        this.notifyExtensionRequestListeners();
    }

    /**
     * Handle new justification from desktop (real-time)
     */
    handleNewJustification(payload) {
        console.log(`[TimeLimit] Live update: new justification for ${payload.target_name}`);

        // Add to beginning of list (most recent first)
        this.justifications = [payload, ...this.justifications];

        // Keep list at reasonable size
        if (this.justifications.length > 100) {
            this.justifications = this.justifications.slice(0, 100);
        }

        // Notify listeners
        this.notifyJustificationListeners();
    }

    /**
     * Handle remote block response
     */
    handleRemoteBlockResponse(payload) {
        const resolve = this._pendingRequests.get('remote_block_window');
        if (resolve) {
            this._pendingRequests.delete('remote_block_window');
            resolve(payload);
        }

        if (payload.success) {
            console.log(`[TimeLimit] Remote blocked: ${payload.windowInfo?.processName}`);
        } else {
            console.error('[TimeLimit] Remote block failed:', payload.error);
        }
    }

    /**
     * Handle remote unblock response
     */
    handleRemoteUnblockResponse(payload) {
        const resolve = this._pendingRequests.get('remote_unblock_window');
        if (resolve) {
            this._pendingRequests.delete('remote_unblock_window');
            resolve(payload);
        }

        if (payload.success) {
            console.log('[TimeLimit] Remote unblock successful');
        } else {
            console.error('[TimeLimit] Remote unblock failed:', payload.error);
        }
    }

    /**
     * Handle get enforcement state response
     */
    handleGetEnforcementStateResponse(payload) {
        const resolve = this._pendingRequests.get('get_enforcement_state');
        if (resolve) {
            this._pendingRequests.delete('get_enforcement_state');
            resolve(payload);
        }

        if (payload.success) {
            this.enforcementState = payload;
            this.notifyEnforcementStateListeners();
        }
    }

    /**
     * Handle enforcement state update (real-time broadcast from desktop)
     */
    handleEnforcementStateUpdate(payload) {
        this.enforcementState = payload;
        this.notifyEnforcementStateListeners();
    }

    /**
     * Handle app blocked event (app was terminated due to restriction)
     */
    handleAppBlocked(payload) {
        const { appName, reason, timestamp } = payload;
        console.log(`[TimeLimit] App blocked: ${appName} (reason: ${reason})`);
        this.notifyAppBlockedListeners(payload);
    }

    // =====================================
    // Event Listeners (for React components)
    // =====================================

    /**
     * Subscribe to limit list updates
     * @param {Function} callback - Called when limits change
     * @returns {Function} Unsubscribe function
     */
    onUpdate(callback) {
        this.updateCallbacks.add(callback);
        return () => this.updateCallbacks.delete(callback);
    }

    /**
     * Subscribe to usage updates
     * @param {Function} callback - Called when usage updates
     * @returns {Function} Unsubscribe function
     */
    onUsageUpdate(callback) {
        this.usageCallbacks.add(callback);
        return () => this.usageCallbacks.delete(callback);
    }

    /**
     * Subscribe to state changes
     * @param {Function} callback - Called on state transitions
     * @returns {Function} Unsubscribe function
     */
    onStateChange(callback) {
        this.stateCallbacks.add(callback);
        return () => this.stateCallbacks.delete(callback);
    }

    /**
     * Subscribe to extension request updates (live updates)
     * @param {Function} callback - Called when extension requests change
     * @returns {Function} Unsubscribe function
     */
    onExtensionRequestUpdate(callback) {
        this.extensionRequestCallbacks.add(callback);
        return () => this.extensionRequestCallbacks.delete(callback);
    }

    /**
     * Subscribe to justification updates (live updates)
     * @param {Function} callback - Called when justifications change
     * @returns {Function} Unsubscribe function
     */
    onJustificationUpdate(callback) {
        this.justificationCallbacks.add(callback);
        return () => this.justificationCallbacks.delete(callback);
    }

    /**
     * Subscribe to enforcement state updates (for screen share overlay rendering)
     * @param {Function} callback - Called when enforcement state changes
     * @returns {Function} Unsubscribe function
     */
    onEnforcementStateUpdate(callback) {
        this.enforcementStateCallbacks.add(callback);
        return () => this.enforcementStateCallbacks.delete(callback);
    }

    /**
     * Subscribe to app blocked events
     * @param {Function} callback - Called when an app is blocked
     * @returns {Function} Unsubscribe function
     */
    onAppBlocked(callback) {
        this.appBlockedCallbacks.add(callback);
        return () => this.appBlockedCallbacks.delete(callback);
    }

    notifyUpdateListeners() {
        const data = {
            limits: this.limits,
            justifications: this.justifications,
            extensionRequests: this.extensionRequests,
            currentApp: this.currentApp,
            isLoading: this.isLoading,
            isRunning: this.isRunning
        };

        for (const callback of this.updateCallbacks) {
            try {
                callback(data);
            } catch (error) {
                console.error('[TimeLimit] Error in update callback:', error);
            }
        }
    }

    notifyUsageListeners() {
        for (const callback of this.usageCallbacks) {
            try {
                callback(this.usage);
            } catch (error) {
                console.error('[TimeLimit] Error in usage callback:', error);
            }
        }
    }

    notifyStateListeners(changes) {
        for (const callback of this.stateCallbacks) {
            try {
                callback(changes);
            } catch (error) {
                console.error('[TimeLimit] Error in state callback:', error);
            }
        }
    }

    notifyExtensionRequestListeners() {
        for (const callback of this.extensionRequestCallbacks) {
            try {
                callback(this.extensionRequests);
            } catch (error) {
                console.error('[TimeLimit] Error in extension request callback:', error);
            }
        }
        // Also notify general update listeners
        this.notifyUpdateListeners();
    }

    notifyJustificationListeners() {
        for (const callback of this.justificationCallbacks) {
            try {
                callback(this.justifications);
            } catch (error) {
                console.error('[TimeLimit] Error in justification callback:', error);
            }
        }
        // Also notify general update listeners
        this.notifyUpdateListeners();
    }

    notifyEnforcementStateListeners() {
        for (const callback of this.enforcementStateCallbacks) {
            try {
                callback(this.enforcementState);
            } catch (error) {
                console.error('[TimeLimit] Error in enforcement state callback:', error);
            }
        }
    }

    notifyAppBlockedListeners(payload) {
        for (const callback of this.appBlockedCallbacks) {
            try {
                callback(payload);
            } catch (error) {
                console.error('[TimeLimit] Error in app blocked callback:', error);
            }
        }
    }

    // =====================================
    // Time Manipulation
    // =====================================

    /**
     * Get all manipulation configs
     */
    getManipulationConfigs() {
        console.log('[TimeLimit] Requesting manipulation configs...');
        return this.sendMessage('get_manipulation_configs', {});
    }

    handleGetManipulationConfigsResponse(payload) {
        if (payload.success) {
            this.manipulationConfigs = payload.configs || {};
            this.notifyManipulationListeners();
        }
    }

    /**
     * Set a manipulation config by type
     * @param {string} type - Config type (game_rewards, time_auction, etc.)
     * @param {Object} config - Config data
     * @returns {Promise<Object>} Result
     */
    async setManipulationConfig(type, config) {
        console.log(`[TimeLimit] Setting manipulation config: ${type}`);
        return new Promise((resolve) => {
            this.sendMessage('set_manipulation_config', { type, config });
            this._pendingRequests.set('set_manipulation_config', resolve);
        });
    }

    handleSetManipulationConfigResponse(payload) {
        const resolve = this._pendingRequests.get('set_manipulation_config');
        if (resolve) {
            this._pendingRequests.delete('set_manipulation_config');
            resolve(payload);
        }
        if (payload.success) {
            this.getManipulationConfigs();
        }
    }

    /**
     * Purchase time via auction
     * @param {number} minutes - Minutes to purchase
     * @param {string} currencyType - Currency type
     * @returns {Promise<Object>} Result with cost, remaining purchases
     */
    async purchaseTime(minutes, currencyType) {
        console.log(`[TimeLimit] Purchasing ${minutes}m of time`);
        return new Promise((resolve) => {
            this.sendMessage('purchase_time', { minutes, currencyType });
            this._pendingRequests.set('purchase_time', resolve);
        });
    }

    handlePurchaseTimeResponse(payload) {
        const resolve = this._pendingRequests.get('purchase_time');
        if (resolve) {
            this._pendingRequests.delete('purchase_time');
            resolve(payload);
        }
    }

    /**
     * Apply a retroactive limit
     * @param {Object} config - { targetType, targetName, limitSeconds, period }
     * @returns {Promise<Object>} Result with alreadyOver, usedSeconds, limitSeconds
     */
    async applyRetroactiveLimit(config) {
        console.log(`[TimeLimit] Applying retroactive limit:`, config);
        return new Promise((resolve) => {
            this.sendMessage('apply_retroactive_limit', config);
            this._pendingRequests.set('apply_retroactive_limit', resolve);
        });
    }

    handleApplyRetroactiveLimitResponse(payload) {
        const resolve = this._pendingRequests.get('apply_retroactive_limit');
        if (resolve) {
            this._pendingRequests.delete('apply_retroactive_limit');
            resolve(payload);
        }
    }

    /**
     * Handle live manipulation config update
     */
    handleManipulationUpdate(payload) {
        this.manipulationConfigs = payload || {};
        this.notifyManipulationListeners();
    }

    /**
     * Subscribe to manipulation config updates
     * @param {Function} callback
     * @returns {Function} Unsubscribe function
     */
    onManipulationUpdate(callback) {
        this.manipulationCallbacks.add(callback);
        return () => this.manipulationCallbacks.delete(callback);
    }

    notifyManipulationListeners() {
        for (const callback of this.manipulationCallbacks) {
            try {
                callback(this.manipulationConfigs);
            } catch (error) {
                console.error('[TimeLimit] Error in manipulation callback:', error);
            }
        }
    }

    /**
     * Get current manipulation configs
     */
    getManipulationConfigsData() {
        return this.manipulationConfigs;
    }

    // =====================================
    // Screen-Time Overlay Config
    // =====================================

    /**
     * Fetch current screen-time overlay config from desktop.
     * Returns { enabled, position, hasSessionLimit, visible }
     */
    async getScreenTimeOverlayConfig() {
        return new Promise((resolve) => {
            this.sendMessage('get_screen_time_overlay_config', {});
            this._pendingRequests.set('get_screen_time_overlay_config', resolve);
        });
    }

    handleGetScreenTimeOverlayConfigResponse(payload) {
        const resolve = this._pendingRequests.get('get_screen_time_overlay_config');
        if (resolve) {
            this._pendingRequests.delete('get_screen_time_overlay_config');
            resolve(payload);
        }
    }

    /**
     * Update screen-time overlay config on desktop.
     * @param {{enabled?: boolean, position?: 'top-left'|'top-right'|'bottom-left'|'bottom-right'}} config
     */
    async setScreenTimeOverlayConfig(config) {
        return new Promise((resolve) => {
            this.sendMessage('set_screen_time_overlay_config', config || {});
            this._pendingRequests.set('set_screen_time_overlay_config', resolve);
        });
    }

    handleSetScreenTimeOverlayConfigResponse(payload) {
        const resolve = this._pendingRequests.get('set_screen_time_overlay_config');
        if (resolve) {
            this._pendingRequests.delete('set_screen_time_overlay_config');
            resolve(payload);
        }
    }

    // =====================================
    // Getters
    // =====================================

    getLimitsList() {
        return this.limits;
    }

    getUsageData() {
        return this.usage;
    }

    getJustificationsList() {
        return this.justifications;
    }

    getCurrentApp() {
        return this.currentApp;
    }

    getIsLoading() {
        return this.isLoading;
    }

    getIsRunning() {
        return this.isRunning;
    }

    getEnforcementStateData() {
        return this.enforcementState;
    }

    /**
     * Get usage for a specific app
     * @param {string} appName - App name (e.g., "chrome.exe")
     * @returns {number} Seconds used
     */
    getAppUsage(appName) {
        return this.usage[`app:${appName}`] || 0;
    }

    /**
     * Get limit for a specific app
     * @param {string} appName - App name
     * @returns {Object|null} Limit config or null
     */
    getAppLimit(appName) {
        return this.limits.find(l =>
            l.target_type === 'app' && l.target_name === appName
        ) || null;
    }

    // =====================================
    // Utility Functions
    // =====================================

    /**
     * Format seconds as human-readable time
     * @param {number} seconds - Seconds
     * @returns {string} Formatted time (e.g., "1h 30m")
     */
    static formatTime(seconds) {
        if (seconds < 0) seconds = 0;

        const hours = Math.floor(seconds / 3600);
        const minutes = Math.floor((seconds % 3600) / 60);
        const secs = Math.floor(seconds % 60);

        if (hours > 0) {
            return `${hours}h ${minutes}m`;
        } else if (minutes > 0) {
            return `${minutes}m ${secs}s`;
        }
        return `${secs}s`;
    }

    /**
     * Calculate remaining time percentage
     * @param {number} used - Seconds used
     * @param {number} limit - Limit in seconds
     * @returns {number} Percentage remaining (0-100)
     */
    static getRemainingPercent(used, limit) {
        if (limit <= 0) return 100;
        const remaining = Math.max(0, limit - used);
        return (remaining / limit) * 100;
    }

    // =====================================
    // Cleanup
    // =====================================

    async cleanup() {
        console.log('[TimeLimit] Cleaning up...');
        this.limits = [];
        this.usage = {};
        this.justifications = [];
        this.extensionRequests = [];
        this.currentApp = null;
        this.enforcementState = { activeOverlays: [] };
        this.manipulationConfigs = {};
        this.updateCallbacks.clear();
        this.usageCallbacks.clear();
        this.stateCallbacks.clear();
        this.extensionRequestCallbacks.clear();
        this.justificationCallbacks.clear();
        this.enforcementStateCallbacks.clear();
        this.appBlockedCallbacks.clear();
        this.manipulationCallbacks.clear();
        this._pendingRequests.clear();
    }
}
